// src/pages/kanban/components/ConfigurarTableroModal.tsx
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Badge,
} from '@/components/ui';
import { CheckboxWithLabel } from '@/components/ui/Checkbox';
import {
  GripVertical,
  Eye,
  EyeOff,
  Palette,
  AlertTriangle,
} from 'lucide-react';
import {
  useActualizarColumna,
  useReordenarColumnas,
} from '@/hooks/queries/useKanban';
import type { ColumnaConfig, TableroKanbanData } from '@/types/kanban';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  tableroData: TableroKanbanData;
}

interface ColumnaEditState {
  valor: string;
  nombre: string;
  color: string;
  limite_wip: number | undefined;
  es_oculta: boolean;
}

const COLORES_PRESET = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#6b7280', // gray
];

const CAMPOS_VISIBLES_OPTIONS = [
  { value: 'prioridad', label: 'Prioridad' },
  { value: 'asignado', label: 'Asignado' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'proyecto', label: 'Proyecto' },
  { value: 'fecha_limite', label: 'Fecha limite' },
  { value: 'etiquetas', label: 'Etiquetas' },
  { value: 'comentarios', label: 'Contador comentarios' },
  { value: 'adjuntos', label: 'Contador adjuntos' },
];

export function ConfigurarTableroModal({ open, onClose, tableroData }: Props) {
  const [columnas, setColumnas] = useState<ColumnaEditState[]>([]);
  const [camposVisibles, setCamposVisibles] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const actualizarColumna = useActualizarColumna();
  const reordenarColumnas = useReordenarColumnas();

  const tablero = tableroData.tablero;

  useEffect(() => {
    if (tableroData) {
      // Inicializar columnas desde la configuracion
      const columnasEdit: ColumnaEditState[] = tableroData.columnas.map((col) => {
        const config = tablero.columnas_config?.find((c) => c.valor === col.valor);
        return {
          valor: col.valor,
          nombre: config?.nombre || col.nombre,
          color: config?.color || col.color || '',
          limite_wip: config?.limite_wip ?? col.limite_wip,
          es_oculta: config?.es_oculta ?? false,
        };
      });
      setColumnas(columnasEdit);
      setCamposVisibles(tablero.campos_visibles || ['prioridad', 'asignado', 'comentarios', 'adjuntos']);
    }
  }, [tableroData, open]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newColumnas = [...columnas];
    const [removed] = newColumnas.splice(draggedIndex, 1);
    newColumnas.splice(index, 0, removed);
    setColumnas(newColumnas);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const updateColumna = (index: number, updates: Partial<ColumnaEditState>) => {
    setColumnas((prev) =>
      prev.map((col, i) => (i === index ? { ...col, ...updates } : col))
    );
  };

  const toggleCampoVisible = (campo: string) => {
    setCamposVisibles((prev) =>
      prev.includes(campo) ? prev.filter((c) => c !== campo) : [...prev, campo]
    );
  };

  const handleSave = async () => {
    try {
      // Guardar orden de columnas
      await reordenarColumnas.mutateAsync({
        tableroId: tablero.id,
        orden: columnas.map((c) => c.valor),
      });

      // Guardar configuracion de cada columna
      for (const columna of columnas) {
        const config: Partial<ColumnaConfig> = {
          nombre: columna.nombre,
          color: columna.color || undefined,
          limite_wip: columna.limite_wip,
          es_oculta: columna.es_oculta,
        };
        await actualizarColumna.mutateAsync({
          tableroId: tablero.id,
          columna: columna.valor,
          config,
        });
      }

      onClose();
    } catch {
      // Error handled by react-query
    }
  };

  const isLoading = actualizarColumna.isPending || reordenarColumnas.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Configurar Tablero</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Seccion de columnas */}
          <div>
            <Label className="text-base font-semibold">Columnas</Label>
            <p className="text-sm text-gray-500 mb-3">
              Arrastra para reordenar. Configura visibilidad, limites WIP y colores.
            </p>

            <div className="space-y-2">
              {columnas.map((columna, index) => (
                <div
                  key={columna.valor}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border bg-white cursor-move',
                    draggedIndex === index && 'opacity-50 border-blue-400',
                    columna.es_oculta && 'opacity-60 bg-gray-50'
                  )}
                >
                  <GripVertical className="h-5 w-5 text-gray-400 flex-shrink-0" />

                  {/* Color indicator */}
                  <div
                    className="w-4 h-4 rounded-full border flex-shrink-0"
                    style={{ backgroundColor: columna.color || '#e5e7eb' }}
                  />

                  {/* Nombre */}
                  <Input
                    value={columna.nombre}
                    onChange={(e) => updateColumna(index, { nombre: e.target.value })}
                    className="flex-1 h-8"
                  />

                  {/* Limite WIP */}
                  <div className="flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      min={0}
                      placeholder="WIP"
                      value={columna.limite_wip ?? ''}
                      onChange={(e) =>
                        updateColumna(index, {
                          limite_wip: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      className="w-16 h-8"
                    />
                  </div>

                  {/* Color picker */}
                  <div className="relative group">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <Palette className="h-4 w-4" />
                    </Button>
                    <div className="absolute right-0 top-full mt-1 p-2 bg-white border rounded-lg shadow-lg hidden group-hover:flex gap-1 z-10">
                      {COLORES_PRESET.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={cn(
                            'w-6 h-6 rounded-full border-2 transition-transform hover:scale-110',
                            columna.color === color ? 'border-gray-900' : 'border-transparent'
                          )}
                          style={{ backgroundColor: color }}
                          onClick={() => updateColumna(index, { color })}
                        />
                      ))}
                      <button
                        type="button"
                        className={cn(
                          'w-6 h-6 rounded-full border-2 transition-transform hover:scale-110',
                          !columna.color ? 'border-gray-900' : 'border-gray-300'
                        )}
                        style={{ backgroundColor: '#e5e7eb' }}
                        onClick={() => updateColumna(index, { color: '' })}
                      />
                    </div>
                  </div>

                  {/* Visibilidad */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => updateColumna(index, { es_oculta: !columna.es_oculta })}
                  >
                    {columna.es_oculta ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Seccion de campos visibles */}
          <div>
            <Label className="text-base font-semibold">Campos visibles en tarjetas</Label>
            <p className="text-sm text-gray-500 mb-3">
              Selecciona qué información mostrar en cada tarjeta.
            </p>

            <div className="grid grid-cols-2 gap-2">
              {CAMPOS_VISIBLES_OPTIONS.map((campo) => (
                <CheckboxWithLabel
                  key={campo.value}
                  checked={camposVisibles.includes(campo.value)}
                  onCheckedChange={() => toggleCampoVisible(campo.value)}
                  label={campo.label}
                />
              ))}
            </div>
          </div>

          {/* Vista previa */}
          <div>
            <Label className="text-base font-semibold">Vista previa de columnas</Label>
            <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
              {columnas
                .filter((c) => !c.es_oculta)
                .map((columna) => (
                  <Badge
                    key={columna.valor}
                    className="whitespace-nowrap"
                    style={{
                      borderLeftWidth: '4px',
                      borderLeftColor: columna.color || '#e5e7eb',
                    }}
                  >
                    {columna.nombre}
                    {columna.limite_wip && ` (${columna.limite_wip})`}
                  </Badge>
                ))}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Guardando...' : 'Guardar configuracion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
